import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { invalidateShipmentLists } from '#/entities/shipment'
import { PostAction, PostStatus } from '#/shared/api/generated/shipment/models'
import type {
  LoadboardActionResult,
  LoadboardChoice,
  ShipmentPostResponse,
} from '#/shared/api/generated/shipment/models'
import {
  postShipmentToLoadboardApiV1ShipmentsShipmentIdPostPost,
  usePostShipmentToLoadboardApiV1ShipmentsShipmentIdPostPost,
} from '#/shared/api/generated/shipment/theCargoShipment'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { startCdOAuthConnect } from '#/shared/lib/cd-oauth'
import { toast } from '#/shared/ui/sonner'

const ACTION_TOAST_KEYS: Record<
  PostAction,
  { success: string; error: string }
> = {
  [PostAction.post]: {
    success: 'detailToolbar.postSuccess',
    error: 'detailToolbar.postError',
  },
  [PostAction.repost]: {
    success: 'detailToolbar.repostSuccess',
    error: 'detailToolbar.repostError',
  },
  [PostAction.delete]: {
    success: 'detailToolbar.deleteSuccess',
    error: 'detailToolbar.deleteError',
  },
}

function extractPostResponse(error: unknown): ShipmentPostResponse | null {
  if (typeof error !== 'object' || error === null) return null
  const data = (error as { response?: { data?: unknown } }).response?.data
  if (typeof data !== 'object' || data === null) return null
  const shaped = data as Partial<ShipmentPostResponse>
  if (!Array.isArray(shaped.results)) return null
  return shaped as ShipmentPostResponse
}

function findReauthResult(
  response: ShipmentPostResponse,
): LoadboardActionResult | undefined {
  return response.results.find((result) => result.requires_reauth)
}

function findFailedResult(
  response: ShipmentPostResponse,
): LoadboardActionResult | undefined {
  return response.results.find((result) => result.status === PostStatus.failed)
}

function findUnsuccessfulResult(
  response: ShipmentPostResponse,
): LoadboardActionResult | undefined {
  return response.results.find((result) => result.status !== PostStatus.posted)
}

function hasSuccessfulResult(response: ShipmentPostResponse): boolean {
  return response.results.some((result) => result.status === PostStatus.posted)
}

interface UsePostToLoadboardOptions {
  onPosted?: () => void
  onRemoved?: () => void
}

export function usePostToLoadboard(
  shipmentId: string | undefined,
  options: UsePostToLoadboardOptions = {},
) {
  const { onPosted, onRemoved } = options
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const mutation = usePostShipmentToLoadboardApiV1ShipmentsShipmentIdPostPost()

  const callApi = async (
    action: PostAction,
    loadboards: LoadboardChoice,
    id: string,
  ): Promise<ShipmentPostResponse> => {
    const body = { loadboards, action }
    try {
      const result = await mutation.mutateAsync({ shipmentId: id, data: body })
      return result.data
    } catch (error) {
      const fromBody = extractPostResponse(error)
      if (fromBody) return fromBody
      throw error
    }
  }

  const callApiDirect = async (
    action: PostAction,
    loadboards: LoadboardChoice,
    id: string,
  ): Promise<ShipmentPostResponse> => {
    const body = { loadboards, action }
    try {
      const result =
        await postShipmentToLoadboardApiV1ShipmentsShipmentIdPostPost(id, body)
      return result.data
    } catch (error) {
      const fromBody = extractPostResponse(error)
      if (fromBody) return fromBody
      throw error
    }
  }

  const performMutation = async (
    action: PostAction,
    loadboards: LoadboardChoice,
    id: string,
  ): Promise<ShipmentPostResponse> => {
    let response = await callApi(action, loadboards, id)
    if (findReauthResult(response)) {
      await startCdOAuthConnect()
      response = await callApiDirect(action, loadboards, id)
    }
    return response
  }

  const reportFailure = (
    response: ShipmentPostResponse,
    errorKey: string,
  ): boolean => {
    const failed = findFailedResult(response)
    if (failed) {
      toast.error(failed.error ?? t(errorKey))
      return true
    }
    if (!hasSuccessfulResult(response)) {
      const issue = findUnsuccessfulResult(response)
      toast.error(issue?.error ?? t(errorKey))
      return true
    }
    return false
  }

  const invalidateAfter = (action: PostAction, id: string) => {
    queryClient.invalidateQueries({
      queryKey: [`/api/v1/shipments/${id}`],
    })
    if (action === PostAction.post || action === PostAction.delete) {
      invalidateShipmentLists(queryClient)
    }
  }

  const run = async (
    action: PostAction,
    loadboards: LoadboardChoice,
    idOverride?: string,
  ) => {
    const id = idOverride ?? shipmentId
    if (!id) return
    const keys = ACTION_TOAST_KEYS[action]
    try {
      const response = await performMutation(action, loadboards, id)
      if (reportFailure(response, keys.error)) return
      invalidateAfter(action, id)
      toast.success(t(keys.success))
      if (action === PostAction.post) onPosted?.()
      if (action === PostAction.delete) onRemoved?.()
    } catch (error) {
      toast.error(getApiErrorDetail(error) ?? t(keys.error))
    }
  }

  const pendingAction = mutation.isPending
    ? mutation.variables.data.action
    : undefined
  const pendingLoadboards = mutation.isPending
    ? mutation.variables.data.loadboards
    : undefined

  return {
    post: (loadboards: LoadboardChoice, idOverride?: string) =>
      run(PostAction.post, loadboards, idOverride),
    repost: (loadboards: LoadboardChoice, idOverride?: string) =>
      run(PostAction.repost, loadboards, idOverride),
    remove: (loadboards: LoadboardChoice, idOverride?: string) =>
      run(PostAction.delete, loadboards, idOverride),
    isPending: mutation.isPending,
    isReposting: pendingAction === PostAction.repost,
    isRemoving: pendingAction === PostAction.delete,
    pendingAction,
    pendingLoadboards,
  }
}
